import * as ToggleMenuFlyoutEvent from '@ori-events/toggle-menu-flyout';
import * as UserAction from '@ori-events/user-action';
import { Notification, NotificationFilled } from '@ori-ui/icons';
import { Badge, IconButton, styled } from '@ori-ui/mui';
import { useToken } from '@ori/auth';
import type { VFC } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { CLICK_NOTIFICATION_ICON, DESKTOP_BREAKPOINT } from '~/constants';
import { useAppConfigurationContext } from '~/contexts/AppConfigurationContext';
import { useNotificationsContext } from '~/contexts/NotificationsContext';
import { dispatchToggleMenu } from '~/helpers';
import { useHover } from '~/hooks';

const NotificationBellIconRoot = styled('div')(({ theme }) => ({
  marginLeft: 'auto',

  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(6),
  },

  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    marginLeft: 'auto',
  },

  [theme.breakpoints.up('lg')]: {
    marginLeft: theme.spacing(4),
  },

  [theme.breakpoints.up('xl')]: {
    marginLeft: theme.spacing(6),
  },
}));

const NotificationBellIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.grey.A700,
  fontSize: theme.typography.h4.fontSize,
  padding: 0,

  '&:hover, &:focus, &:active': {
    background: 'none',
    color: theme.palette.grey.A700,
  },
}));

export const NotificationBellIcon: VFC = () => {
  const [ref, Icon] = useHover(NotificationFilled, Notification);
  const [isClicked, setIsClicked] = useState(false);
  const { shouldDisplayNotificationIcon, tenant } = useAppConfigurationContext();
  const { customerId, isAnonymousUser } = useToken();
  const { notificationsCount, loadNotificationsCount } = useNotificationsContext();
  const isVisible = shouldDisplayNotificationIcon && !isAnonymousUser;

  useEffect(() => {
    const abortController = new AbortController();

    if (isVisible) {
      loadNotificationsCount(customerId, tenant, {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any -- microsoft's implementation of AbortSignalLike is not type compatible with native AbortSignal
        abortSignal: abortController.signal as any,
      }).catch((error: Error) => {
        // No need to do anything if we aborted in cleanup function
        if (error.name === 'AbortError') {
          return;
        }

        throw error;
      });
    }

    return () => {
      // Abort previous
      abortController.abort();
    };
  }, [isVisible, customerId, tenant, loadNotificationsCount]);

  const handleClick = useCallback(() => {
    UserAction.dispatch(CLICK_NOTIFICATION_ICON);
    setIsClicked(true);

    dispatchToggleMenu(ToggleMenuFlyoutEvent.MenuTypes.Notifications);
  }, []);

  if (!isVisible) {
    return null;
  }

  return (
    <NotificationBellIconRoot>
      <NotificationBellIconButton
        ref={ref}
        onClick={handleClick}
      >
        <Badge
          color="error"
          badgeContent={notificationsCount}
          max={99}
          invisible={isClicked || notificationsCount === 0}
        >
          <Icon fontSize="inherit" />
        </Badge>
      </NotificationBellIconButton>
    </NotificationBellIconRoot>
  );
};
